<template>
  <div>
    <header class="flex align-items-center justify-content-between mb-3">
      <div>
        <h2 class="m-0 p-0 font-light text-600">{{ pageTitle }}</h2>
        <p v-if="rosterIds.length" class="m-0 pt-1 text-xs text-300">
          Managing {{ rosterIds.length }} {{ rosterIds.length === 1 ? 'record' : 'records' }}.
        </p>
      </div>
      <Button label="Back" icon="pi pi-arrow-left" class="surface-300 hover:surface-500" @click="handleBack" />
    </header>

    <div v-if="routeError" class="card">
      <Message severity="error" :closable="false">{{ routeError }}</Message>
    </div>

    <div v-else class="card overflow-x-auto">
      <RosterUpdateDialog
        :selectedRosterIds="rosterIds"
        :viewType="resolvedViewType"
        @loaded="handleEditorLoaded"
        @updateSuccess="handleUpdateSuccess"
        @handleBack="handleBack"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Message from 'primevue/message';
import RosterUpdateDialog from '@/components/dialogs/RosterUpdateDialog.vue';
import {
  isRosterFilteredRoute,
  parseRosterRouteFilters
} from '@/composables/useRosterRouteFilters';
import { parseCalendarRouteFilters } from '@/composables/useCalendarRouteFilters';
import { parseDashboardRouteDate } from '@/composables/useDashboardRoute';

const MAX_ROSTER_IDS = 100;

const route = useRoute();
const router = useRouter();
const resolvedViewType = ref(null);

const parsedRoute = computed(() => {
  const rawRosterIds = route.params.rosterIds;
  if (typeof rawRosterIds !== 'string' || rawRosterIds.trim() === '') {
    return { rosterIds: [], error: 'No roster IDs were provided.' };
  }

  const idSegments = rawRosterIds.split(',');
  if (idSegments.some(segment => !/^\d+$/.test(segment))) {
    return { rosterIds: [], error: 'Roster IDs must be positive integers separated by commas.' };
  }

  const uniqueRosterIds = Array.from(new Set(idSegments.map(Number)));
  if (uniqueRosterIds.some(rosterId => !Number.isSafeInteger(rosterId) || rosterId <= 0)) {
    return { rosterIds: [], error: 'One or more roster IDs are invalid.' };
  }

  if (uniqueRosterIds.length > MAX_ROSTER_IDS) {
    return {
      rosterIds: [],
      error: `A maximum of ${MAX_ROSTER_IDS} roster records can be edited at once.`
    };
  }

  return { rosterIds: uniqueRosterIds, error: null };
});

const rosterIds = computed(() => parsedRoute.value.rosterIds);
const routeError = computed(() => parsedRoute.value.error);

const pageTitle = computed(() => {
  const itemName = resolvedViewType.value === 'leaves' ? 'Leave' : 'Roster';
  return rosterIds.value.length === 1 ? `Edit ${itemName}` : `Edit ${itemName} Records`;
});

const fallbackRoute = computed(() => ({
  name: resolvedViewType.value === 'leaves' ? 'ViewLeaves' : 'ViewRoster'
}));

const safeReturnTo = computed(() => {
  const returnTo = route.query.returnTo;
  if (
    typeof returnTo !== 'string'
    || !returnTo.startsWith('/')
    || returnTo.startsWith('//')
  ) {
    return null;
  }

  const resolvedRoute = router.resolve(returnTo);
  if (resolvedRoute.name === 'DashboardDay') {
    const parsedDate = parseDashboardRouteDate(resolvedRoute.params);
    return parsedDate.valid ? resolvedRoute.fullPath : null;
  }

  if (
    resolvedRoute.name === 'ViewLeavesCalendarFiltered'
    || resolvedRoute.name === 'ViewCoverCalendarFiltered'
  ) {
    const parsedCalendarFilters = parseCalendarRouteFilters(resolvedRoute.params);
    return parsedCalendarFilters.valid ? resolvedRoute.fullPath : null;
  }

  if (!isRosterFilteredRoute(resolvedRoute.name)) {
    return null;
  }

  const returnViewType = resolvedRoute.name === 'ViewLeavesFiltered'
    ? 'leaves'
    : 'roster';
  const parsedFilters = parseRosterRouteFilters(resolvedRoute.params, returnViewType);
  return parsedFilters.valid ? resolvedRoute.fullPath : null;
});

const handleEditorLoaded = ({ viewType }) => {
  resolvedViewType.value = viewType;
};

const navigateBackOrFallback = (replaceFallback = false) => {
  if (safeReturnTo.value) {
    router.replace(safeReturnTo.value);
    return;
  }

  if (window.history.state?.back) {
    router.back();
    return;
  }

  if (replaceFallback) {
    router.replace(fallbackRoute.value);
  } else {
    router.push(fallbackRoute.value);
  }
};

const handleBack = () => navigateBackOrFallback(false);

const handleUpdateSuccess = ({ viewType } = {}) => {
  if (viewType) {
    resolvedViewType.value = viewType;
  }
  navigateBackOrFallback(true);
};
</script>
